<template>
  <div class="charter-results no-shadow !pb-0 flex flex-col mt-6">
    <ScreenWidthProvider
      v-slot="{ isWider }"
      class="relative"
      :class="[ isSol ? 'px-1 sm:px-5 -mx-4' : 'px-12 -mx-3' ]"
    >
      <Swiper
        ref="dateSlider"
        class="adjacent-dates-carousel"
        v-bind="sliderOptions"
        :class="{ 'is-deal-finder': isDealFinder }"
        :loading="!months.length"
        :always-show-arrows="!error"
        :horizontal-centered-slides="true"
        :arrow-shadow="isWider('lg')"
        loading-skeleton-animation="skeleton-slides"
        loading-skeleton-class="my-6"
        loading-skeleton-height="70%"
        hide-arrow-when-disabled
        @on-slide-prev-click="goPrev"
        @on-slide-next-click="goNext"
        @active-index-change="updateSlide"
      >
        <div
          v-for="(month, idx) in months"
          :key="`csd_${idx}`"
          class="adjacent-dates-carousel-slide"
          :class="[slideClass(month, idx), { shake, sv }, { 'is-deal-finder': isDealFinder }, {'adjacent-dates-carousel-slide-special-date__open-purchase adjacent-dates-carousel-slide-special-date__open-purchase__light-body' : isSpecialSlide(month)} ]"
          @click="setDate(month)"
        >
          <template v-if="error">
            <div />
          </template>
          <template v-else-if="slideIsLoading(month)">
            <b-skeleton
              class="ml-auto mr-auto"
              height="2rem"
              width="80%"
            />
            <b-skeleton
              class="ml-auto mr-auto block"
              width="50%"
            />
            <b-skeleton
              class="ml-auto mr-auto mt-2"
              height="1.5rem"
              width="85%"
            />
          </template>
          <template v-else-if="isSpecialSlide(month)">
            <div class="adjacent-dates-carousel-slide-special-date__text font-semibold w-full absolute z-[2]">
              <span
                class="rounded-t-lg inline-flex justify-center z-[1]"
                :data-i18n="specialSlideTextKey"
              >{{ $t(specialSlideTextKey) }}</span>
            </div>
            <div class="adjacent-dates-carousel-slide-date">
              <span class="block">
                {{ month.y }}
              </span>
              <span class="text-[1.75rem] font-bold">
                {{ month.m }}
              </span>
            </div>
            <div
              v-if="isDiscounted(month)"
              class="adjacent-dates-carousel-slide-price"
            >
              <span
                class="small strikethrough"
                data-i18n="shortFromPrice"
              >{{ $t('shortFromPrice') }}<span>
                {{ $n(month?.regularPricePerPerson) }}
              </span>
              </span>
            </div>
            <div class="adjacent-dates-carousel-slide-price">
              <strong data-i18n="shortFromPrice">
                <small>
                  {{ $t('shortFromPrice') }}
                </small>
                <span :class="{ 'text-red': isDiscounted(month) }">
                  {{ $n(month?.price) }}
                </span>
              </strong>
            </div>
          </template>
          <template v-else>
            <div class="adjacent-dates-carousel-slide-date">
              <span class="block">
                {{ month.y }}
              </span>
              <span class="text-[1.75rem] font-bold">
                {{ month.m }}
              </span>
            </div>
            <div
              v-if="isDiscounted(month)"
              class="adjacent-dates-carousel-slide-price"
            >
              <span
                class="small strikethrough"
                data-i18n="shortFromPrice"
              >{{ $t('shortFromPrice') }}</span>
              <span>
                {{ $n(month?.regularPricePerPerson) }}
              </span>
            </div>
            <div class="adjacent-dates-carousel-slide-price">
              <strong data-i18n="shortFromPrice">
                <small>
                  {{ $t('shortFromPrice') }}
                </small>
                <span :class="{ 'text-red': isDiscounted(month) }">
                  {{ $n(month?.price) }}
                </span>
              </strong>
            </div>
          </template>
        </div>
      </Swiper>
    </ScreenWidthProvider>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import screenWidthMixin from '@packages/screen/mixins/screen-width.mixin.js'
import { DATEPICKER_DATE_FORMAT } from '@packages/date/formats'

export default defineNuxtComponent({
  mixins: [screenWidthMixin],

  props: {
    loading: {
      type: Boolean,
      default: false,
    },

    error: {
      type: Boolean,
      default: false,
    },

    isDealFinder: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['goToMonth'],

  data () {
    // getters & mapX not available on init
    const isSv = this.locale === 'sv'
    return {
      currentSlide: 0,
      shake: false,
      selectedMonthYear: null,

      sliderOptions: {
        arrowsAlign: 'border',
        arrowsType: 'default',
        arrowsColor: 'dark',
        swiperContainerStyle: 'padding: 0 0.4rem',
        constantDisplayNavigation: true,
        navigation: true,
        pagination: false,
        navigationPreventDefault: true,
        initialSlide: 0,
        slidesPerView: 3,
        spaceBetween: 10,
        breakpoints: isSv ? {
          [getTailwindBreakpointsPx().md]: {
            slidesPerView: 5,
          },

          1366: {
            slidesPerView: 6,
          },

          [getTailwindBreakpointsPx()['2xl']]: {
            slidesPerView: 7,
          },
        } : {
          [getTailwindBreakpointsPx().md]: {
            slidesPerView: 5,
            arrowsType: 'circular',
          },

          [getTailwindBreakpointsPx().sm]: {
            slidesPerView: 3,
            arrowsType: 'circular',
          },

          [getTailwindBreakpointsPx().lg]: {
            slidesPerView: 7,
            arrowsType: 'circular',
            arrowsAlign: 'border',
          },
        },
      },
    }
  },

  computed: {
    ...mapState(useLocaleStore, ['locale']),
    ...mapState(useDealfinderStore, {
      dates: 'dealDates',
      startDate: 'startDate',
    }),

    ...mapState(useDealfinderStore, {
      cheapestDateList: 'cheapestDateList',
      getCurrentActiveIndex: 'currentActiveIndex',
      dealfinderCurrentSlideDate: 'dealfinderCurrentSlideDate',
    }),

    isSpecialSlide () {
      if (!this.$isSol) {
        return () => false
      }
      return (month) => {
        return this.dates.some(date => {
          if (date.recommended_flight) {
            const dateObj = new Date(date.date)
            const monthObj = new Date(month.date)
            const dateMonth = dateObj.getMonth()
            const dateYear = dateObj.getFullYear()
            const monthMonth = monthObj.getMonth()
            const monthYear = monthObj.getFullYear()
            return dateMonth === monthMonth && dateYear === monthYear
          }
          return false
        })
      }
    },
    specialSlideTextKey () {
      return 'recommendedFlight'
    },
    sv () {
      return this.locale === 'sv'
    },

    xlNoSlides () {
      return this.sv ? 5 : 7
    },

    months () {
      if ((this.dates || []).length === 0) {
        return [...new Array(this.xlNoSlides)].map(() => ({ loading: true }))
      }
      return (this.cheapestDateList || [])
    },

    sliderDisplay () {
      return this.screenWidth > 767 ? 6 : 2
    },

    currentActiveIndex () {
      return this.getCurrentActiveIndex(this.selectedMonthYear)
    },
  },

  watch: {
    months () {
      this.handleInitialSlide()
    },

    dealfinderCurrentSlideDate (date) {
      this.setMonth(date)
    },
  },

  mounted () {
    this.setResizeListener()
    this.handleInitialSlide()
  },

  methods: {
    goPrev (currentSlideIndex) {
      if (!this.$refs.dateSlider) {
        return
      }
      if (currentSlideIndex === 0) {
        this.shake = true
        this.timer = window.setTimeout(() => {
          this.shake = false
        }, 500)
      }
      if (!this.$refs.dateSlider) {
        return
      }
      this.$refs.dateSlider.slidePrev()
    },

    goNext (currentSlideIndex) {
      if (!this.$refs.dateSlider) {
        return
      }
      const visibleSlidesCount = this.$refs.dateSlider.getVisibleSlidesCount()
      if (currentSlideIndex + visibleSlidesCount >= this.months.length) {
        this.shake = true
        this.timer = window.setTimeout(() => {
          this.shake = false
        }, 500)
      }
      if (!this.$refs.dateSlider) {
        return
      }
      this.$refs.dateSlider.slideNext()
    },

    handleInitialSlide () {
      const centralSlideIndex = Math.floor(this.sliderDisplay / 2)
      const newInitialSlide = this.currentActiveIndex - centralSlideIndex

      if (newInitialSlide < 0) {
        this.sliderOptions.initialSlide = 0
      } else if (this.currentActiveIndex + centralSlideIndex > this.months.length - 1) {
        this.sliderOptions.initialSlide = (this.months.length - 1) - this.sliderDisplay
      } else {
        this.sliderOptions.initialSlide = newInitialSlide
      }
    },

    updateSlide (idx) {
      this.dateSliderKey += 1
      this.currentSlide = idx
    },

    isDiscounted (date) {
      return !date?.loading && dealfinderUtils.dateIsDiscounted(date)
    },

    setDate (month) {
      this.selectedMonthYear = month.date
      this.$emit('goToMonth', this.$dayjs.unix(month.ts).format(DATEPICKER_DATE_FORMAT))
    },

    setMonth (month) {
      const currentMonth = new Date(this.dealfinderCurrentSlideDate).getMonth()
      const newMonth = new Date(month).getMonth()

      if (newMonth > currentMonth) {
        this.goNext(this.currentSlide)
      } else if (newMonth < currentMonth) {
        this.goPrev(this.currentSlide)
      }

      this.selectedMonthYear = month
    },

    slideIsLoading (month) {
      return this.loading || month?.loading
    },

    slideIndexIsActive (idx) {
      return !this.loading && this.currentActiveIndex === idx
    },

    slideShowBorder (idx) {
      const idxCalc = this.currentActiveIndex !== idx && this.currentActiveIndex !== idx - 1
      return !this.error && !this.loading && idxCalc
    },

    slideClass (month, idx) {
      return !this.error && !this.slideIsLoading(month) && this.slideIndexIsActive(idx)
        ? 'active'
        : 'mt-0' // 3'
    },
  },
})
</script>
